import { FC } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Clock, PoundSterling, Tag, ArrowLeft, Calendar, AlertCircle } from 'lucide-react';
import { useService } from '../hooks/useServices';
import { Button } from '../components/ui/Button';

/**
 * Format price in GBP
 */
function formatPrice(price: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
  }).format(price);
}

/**
 * Format duration in human-readable form
 */
function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  if (remaining === 0) return `${hours} hr`;
  return `${hours} hr ${remaining} min`;
}

/**
 * Loading skeleton for the service detail page
 */
const ServiceDetailSkeleton: FC = () => (
  <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="animate-pulse">
        <div className="h-6 w-32 bg-gray-200 dark:bg-slate-700 rounded mb-8" />
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-8 shadow-sm">
          <div className="h-6 w-24 bg-gray-200 dark:bg-slate-700 rounded-full mb-4" />
          <div className="h-10 w-3/4 bg-gray-200 dark:bg-slate-700 rounded mb-4" />
          <div className="space-y-3 mb-8">
            <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-full" />
            <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-5/6" />
            <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-2/3" />
          </div>
          <div className="flex gap-4 mb-8">
            <div className="h-20 w-40 bg-gray-200 dark:bg-slate-700 rounded-xl" />
            <div className="h-20 w-40 bg-gray-200 dark:bg-slate-700 rounded-xl" />
          </div>
          <div className="h-12 w-48 bg-gray-200 dark:bg-slate-700 rounded-lg" />
        </div>
      </div>
    </div>
  </div>
);

/**
 * ServiceDetailPage - Displays full details for a single massage service
 */
export const ServiceDetailPage: FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { data: service, isLoading, error } = useService(slug || '');

  if (isLoading) {
    return <ServiceDetailSkeleton />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-700 p-8 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" aria-hidden="true" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-slate-100 mb-2">
            Error Loading Service
          </h1>
          <p className="text-gray-600 dark:text-slate-400 mb-6">
            {error.message || 'An unexpected error occurred.'}
          </p>
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Services
          </Link>
        </div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center px-4">
        <Helmet>
          <title>Service Not Found | Massatherapy</title>
        </Helmet>
        <div className="max-w-md w-full backdrop-blur-lg bg-white/80 dark:bg-slate-800/80 rounded-2xl shadow-xl border border-white/20 dark:border-slate-700/50 p-8 text-center">
          <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/30">
            <Tag className="h-10 w-10 text-primary-600 dark:text-primary-400" aria-hidden="true" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100 mb-3">
            Service Not Found
          </h1>
          <p className="text-gray-600 dark:text-slate-400 mb-6">
            The service you are looking for does not exist or may have been removed.
          </p>
          <Link
            to="/services"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Browse All Services
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{service.name} | Massatherapy</title>
        <meta name="description" content={service.description} />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
        <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
          {/* Back link */}
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 font-medium mb-6 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Services
          </Link>

          {/* Main card */}
          <div className="backdrop-blur-lg bg-white/90 dark:bg-slate-800/90 rounded-2xl shadow-lg border border-white/20 dark:border-slate-700/50 overflow-hidden">
            {/* Category badge and title */}
            <div className="p-6 sm:p-8">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-primary-700 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30 px-3 py-1 rounded-full mb-4">
                <Tag className="h-3 w-3" aria-hidden="true" />
                {service.category}
              </span>

              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-slate-100 mb-4">
                {service.name}
              </h1>

              <p className="text-lg text-gray-600 dark:text-slate-400 leading-relaxed">
                {service.description}
              </p>
            </div>

            {/* Details grid */}
            <div className="px-6 sm:px-8 pb-6 sm:pb-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-slate-700/50 rounded-xl">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/40">
                    <PoundSterling className="h-6 w-6 text-primary-600 dark:text-primary-400" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-slate-400">Price</p>
                    <p className="text-xl font-bold text-gray-900 dark:text-slate-100">
                      {formatPrice(service.basePrice)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-slate-700/50 rounded-xl">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/40">
                    <Clock className="h-6 w-6 text-primary-600 dark:text-primary-400" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-slate-400">Duration</p>
                    <p className="text-xl font-bold text-gray-900 dark:text-slate-100">
                      {formatDuration(service.duration)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Book Now CTA */}
              <Button
                variant="primary"
                size="lg"
                onClick={() => {
                  window.location.href = `/book?serviceId=${service.id}`;
                }}
                leftIcon={<Calendar className="h-5 w-5" />}
                className="w-full sm:w-auto"
              >
                Book Now
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

ServiceDetailPage.displayName = 'ServiceDetailPage';

export default ServiceDetailPage;
